// pages/Attendance/index.jsx
// FTO & COA index page — mirrors EPO index pattern exactly.
// Navbar links here; "+ New FTO / COA" buttons navigate to the form.
//
// Pass 2 UI update (2026):
//  - The local <StatCard> component has been REMOVED and the four
//    stat tiles in the top row are now rendered via the shared
//    CompactStatsRow + StatTile from the DashboardKit. Same four
//    metrics (Total / Pending / Approved / Denied), same field
//    mapping off the `stats` state, same loading behavior.
//  - The local <StatusBadge> (boolean approval state → Approved/
//    Denied/Pending pill) is INTENTIONALLY KEPT — the kit's
//    StatusBadge has different semantics (verified/locked/draft/
//    etc.) and the boolean-shaped one here matches the
//    supervisorApproval / hrApproval / mpcApproval API fields
//    exactly. Renaming or replacing it would be a logic change.
//  - The local <TypeBadge> and <IconBtn> are also kept — they
//    don't shadow anything in the kit and are tightly coupled to
//    the FTO type values and per-row action handlers.
//  - All API calls (getFtoStats, getMyFtoRequests,
//    getAwaitingApproval, getAwaitingHrApproval, getSubordinates,
//    getMyCoaRequests, deleteFto) are unchanged.
//  - All useState, useEffect, useMemo hooks and the tab fetch
//    dispatch are unchanged. Delete confirmation still uses
//    window.confirm. Toast notifications still go through
//    react-toastify.
//  - The debug console.log lines for myAuth are kept (they were
//    in the original; not my call to remove them).

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Container, Card, CardBody,
    Input, InputGroup,
    Button, Spinner,
    UncontrolledTooltip,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    DashboardPageHeader,
    CompactStatsRow,
    ResponsiveTabs, TableSkeleton } from "../../Components/Common/DashboardKit";
import useAuth from "./useAuth";
import { inkFor } from "../../utils/themeColor";
import {
    getFtoStats,
    getMyFtoRequests,
    getAwaitingApproval,
    getAwaitingHrApproval,
    getAwaitingCoaApproval,
    getAwaitingCoaHrApproval,
    getSubordinates,
    getMyCoaRequests,
    getCoaAwaitingApproval,
    getCoaAwaitingHrApproval,
    getCoaSubordinates,
    deleteFto,
    deleteCoa,
    voidFtoByHr,
    voidCoaByHr,
    deleteRuleFor,
    updateFtoApprovals,
    updateCoaApprovals,
} from "./ftoService";

// ─── Status badge (boolean approval shape) ─────────────────────────────────
// NOTE: this is intentionally different from DashboardKit/StatusBadge —
// it maps a tri-state boolean (true/false/null) to Approved/Denied/Pending,
// which is the exact shape of supervisorApproval / hrApproval / mpcApproval
// returned by the FTO/COA API. Do not replace with the kit's StatusBadge
// without first mapping those booleans to status keys.
const StatusBadge = ({ val }) => {
    if (val === true) return <span className="badge bg-success-subtle text-success">Approved</span>;
    if (val === false) return <span className="badge bg-danger-subtle text-danger">Denied</span>;
    return <span className="badge bg-warning-subtle text-warning">Pending</span>;
};

// ─── Leave type badge ─────────────────────────────────────────────────────────
const TypeBadge = ({ type, otherReason }) => {
    const map = {
        SCHEDULED: { label: "Scheduled FTO", cls: "bg-primary-subtle text-primary" },
        COMPANY: { label: "Company", cls: "bg-info-subtle text-info" },
        OTHER: { label: otherReason || "Other", cls: "bg-secondary-subtle text-secondary" },
        COA: { label: "COA", cls: "bg-success-subtle text-success" },
    };
    const { label, cls } = map[type] ?? { label: type, cls: "bg-secondary-subtle text-secondary" };
    return <span className={`badge ${cls}`}>{label}</span>;
};

// ─── Format date ──────────────────────────────────────────────────────────────
const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, iconBg, icon }) => (
    <Card className="card-animate">
        <CardBody>
            <div className="d-flex align-items-center justify-content-between">
                <div>
                    <p className="text-uppercase fw-medium text-muted mb-1" style={{ fontSize: 12 }}>{label}</p>
                    <h4 className="mb-0 fw-bold">{value ?? <Spinner size="sm" />}</h4>
                    {sub && <p className="text-muted mb-0 mt-1" style={{ fontSize: 11 }}>{sub}</p>}
                </div>
                <div className="avatar-sm flex-shrink-0">
                    <span className={`avatar-title rounded-circle fs-4 ${iconBg}`}>{icon}</span>
                </div>
            </div>
        </CardBody>
    </Card>
);

// ─── Icon button ──────────────────────────────────────────────────────────────
const IconBtn = ({ id, title, color, onClick, disabled, children }) => (
    <>
        <button
            id={id}
            onClick={onClick}
            disabled={disabled}
            className="btn btn-sm btn-icon"
            style={{
                width: 28, height: 28, padding: 0,
                border: "1px solid var(--ssw-line, #e9ebec)", borderRadius: 5,
                background: "var(--ssw-surface, #fff)", opacity: disabled ? 0.35 : 1,
                cursor: disabled ? "not-allowed" : "pointer", color: inkFor(color),
            }}
        >
            {children}
        </button>
        <UncontrolledTooltip target={id}>{title}</UncontrolledTooltip>
    </>
);

// ─── Tab definitions ──────────────────────────────────────────────────────────
// Phase 3 Pass 3 — added COA-equivalent tabs to mirror FTO behavior.
// The matrix is:
//          FTO                                 COA
//   my     "My FTOs"            (everyone)     "My COAs"            (everyone)
//   awaiting    "Awaiting My Approval (FTO)" (manager)   "Awaiting My Approval (COA)" (manager)
//   awaiting-hr "FTOs Awaiting HR"  (HR)      "COAs Awaiting HR"   (HR)
//   subordinates "Subordinates' FTOs" (manager) "Subordinates' COAs" (manager)
const TABS = [
    { key: "my",                  label: (c) => `My FTOs (${c.my ?? 0})`,                       show: () => true },
    { key: "coa",                 label: (c) => `My COAs (${c.coa ?? 0})`,                      show: () => true },
    // Approval / subordinate queues moved to the shared Approvals page
    // (/attendance/approvals), linked from HR Dashboard + Supervisor menu.
    // Personal Attendance is now personal-only.
];

// ─── Main page ────────────────────────────────────────────────────────────────
const AttendanceIndex = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = useAuth();   // ← must be inside component
    // Phase 3: auth comes from useAuth(). The tab visibility (TABS.show)
    // reads auth.isHR / auth.isSuperUser / auth.isManager, all of which
    // resolve through the EffectiveAccessBundle now. No console logging.
    // Deep-link support: ?tab=awaiting (etc.) opens that tab directly, but
    // only if the current user is allowed to see it (otherwise fall back to
    // "my"). This is how the "Awaiting My Approval" hub card lands here.
    const requestedTab = searchParams.get("tab");
    const initialTab = (() => {
        if (!requestedTab) return "my";
        const def = TABS.find((t) => t.key === requestedTab);
        if (def && (typeof def.show !== "function" || def.show(auth))) return requestedTab;
        return "my";
    })();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [refreshKey, setRefreshKey] = useState(0);
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [tabCounts, setTabCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteType, setDeleteType] = useState("fto");
    const [deleteMode, setDeleteMode] = useState("own");   // "own" = the employee's delete, "hr" = HR / IT Admin removal
    const [deleting, setDeleting] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    // ── Fetch stats ──────────────────────────────────────────────────────────
    useEffect(() => {
        getFtoStats()
            .then(({ data }) => setStats(data))
            .catch(() => { });
    }, []);

    // ── Fetch rows when tab changes ──────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setRows([]);

        const fetcher =
            activeTab === "my"                ? getMyFtoRequests :
            activeTab === "awaiting"          ? getAwaitingApproval :
            activeTab === "awaiting-hr"       ? getAwaitingHrApproval :
            activeTab === "subordinates"      ? getSubordinates :
            activeTab === "awaiting-coa"      ? getCoaAwaitingApproval :
            activeTab === "awaiting-hr-coa"   ? getCoaAwaitingHrApproval :
            activeTab === "subordinates-coa"  ? getCoaSubordinates :
                                                getMyCoaRequests;

        fetcher()
            .then(({ data }) => {
                const items = Array.isArray(data) ? data : (data?.items ?? []);
                setRows(items);
                setTabCounts((p) => ({ ...p, [activeTab]: items.length }));
            })
            .catch(() => toast.error("Failed to load requests."))
            .finally(() => setLoading(false));
    }, [activeTab, refreshKey]);

    // ── Search filter ────────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        if (!search.trim()) return rows;
        const q = search.toLowerCase();
        return rows.filter(r =>
            String(r.id).includes(q) ||
            (r.startDate ?? "").includes(q) ||
            (r.typeOfLeave ?? "").toLowerCase().includes(q) ||
            (r.employeeComment ?? "").toLowerCase().includes(q)
        );
    }, [rows, search]);

    // ── Pagination ───────────────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

    useEffect(() => { setPage(1); }, [search, activeTab]);

    // ── Delete helpers ───────────────────────────────────────────────────────
    const confirmDelete = (id, type, mode) => {
        setDeleteId(id);
        setDeleteType(type);
        setDeleteMode(mode || "own");
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            setDeleting(true);
            // Owner rule (2026-10-01): a reviewed request is removed by HR / IT Admin
            // (the server reverses any balance deduction); an unreviewed one is the
            // employee's own delete.
            if (deleteType === "coa") {
                if (deleteMode === "hr") await voidCoaByHr(deleteId); else await deleteCoa(deleteId);
            } else {
                if (deleteMode === "hr") await voidFtoByHr(deleteId); else await deleteFto(deleteId);
            }
            setRows(p => p.filter(r => r.id !== deleteId));
            toast.success("Request deleted.");
            setDeleteModal(false);
        } catch (e) {
            const msg = e?.response?.data?.message;
            toast.error(msg ? `Could not delete request: ${msg}` : "Could not delete request.");
        } finally {
            setDeleting(false);
            setDeleteId(null);
        }
    };

    // Any COA-related tab. The table layout differs (modified time column,
    // no MPC, etc) so several rendering branches gate on this.
    const isCOATab = activeTab === "coa"
        || activeTab === "awaiting-coa"
        || activeTab === "awaiting-hr-coa"
        || activeTab === "subordinates-coa";

    // Approval tabs show inline Approve / Reject buttons. "awaiting"/"awaiting-coa"
    // set the SUPERVISOR gate; "awaiting-hr"/"awaiting-hr-coa" set the HR gate.
    const isSupervisorQueue = activeTab === "awaiting" || activeTab === "awaiting-coa";
    const isHrQueue         = activeTab === "awaiting-hr" || activeTab === "awaiting-hr-coa";
    const isApprovalQueue   = isSupervisorQueue || isHrQueue;

    // Inline approve / reject from the queue, so an approver doesn't have to
    // open every request. Sets only the gate relevant to the current queue;
    // the backend authorizes based on the caller's role. On success we bump
    // refreshKey so the row leaves the queue.
    const handleQuickDecision = async (id, approved) => {
        try {
            const payload = isHrQueue
                ? { hrApproval: approved }
                : { supervisorApproval: approved };
            if (isCOATab) {
                await updateCoaApprovals(id, payload);
            } else {
                await updateFtoApprovals(id, payload);
            }
            toast.success(approved ? "Request approved." : "Request rejected.");
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Could not update approval. You may not have rights on this request.");
        }
    };


    // ── Stats array shaped for CompactStatsRow / StatTile ──────────────────────
    // Loading is true until `getFtoStats` resolves (stats === null). Field
    // names (total / pending / approved / denied) match the API payload and
    // must not be renamed.
    // Labels absorb the most useful bits of the legacy `sub` text the
    // local StatCard used to show:
    //   "Total FTOs" + sub "All time"           → "Total FTOs"
    //   "Pending"    + sub "Awaiting approval"  → "Pending"
    //   "Approved"   + sub "This year"          → "Approved (YTD)"
    //   "Denied"     + sub "This year"          → "Denied (YTD)"
    // The YTD framing is the only piece that wasn't already implied by
    // the label, so we keep it.
    const statsLoading = stats === null;
    const statTiles = [
        {
            key: "total",
            label: "Total FTOs",
            value: stats?.total ?? 0,
            icon: "ri-calendar-line",
            tone: "primary",
            loading: statsLoading,
        },
        {
            key: "pending",
            label: "Pending",
            value: stats?.pending ?? 0,
            icon: "ri-time-line",
            tone: "warning",
            loading: statsLoading,
        },
        {
            key: "approved",
            label: "Approved (YTD)",
            value: stats?.approved ?? 0,
            icon: "ri-checkbox-circle-line",
            tone: "success",
            loading: statsLoading,
        },
        {
            key: "denied",
            label: "Denied (YTD)",
            value: stats?.denied ?? 0,
            icon: "ri-close-circle-line",
            tone: "danger",
            loading: statsLoading,
        },
    ];

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-calendar-2-line"
                    eyebrow="Personal Attendance"
                    title="FTO & COA"
                    subtitle="File and track Flexible Time Off and Change of Attendance requests."
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Personal Attendance", to: "/workflows?wf=pac" },
                        { label: "FTO & COA" },
                    ]}
                />

                {/* ── Stat tiles via shared DashboardKit ────────────────────────── */}
                <CompactStatsRow
                    stats={statTiles}
                    columns={{ xl: 3, md: 6 }}
                    className="mb-3"
                />

                {/* ── Main card ───────────────────────────────────────────────── */}
                <Card>
                    <CardBody>

                        {/* Header */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="card-title mb-0">My Requests</h5>
                            <div className="d-flex gap-2">
                                <Button color="primary" size="sm" onClick={() => navigate("/attendance/fto/new")}>
                                    <i className="ri-add-line me-1" />New FTO Request
                                </Button>
                                {/* Owner rule (2026-10-02): COA is for salaried employees only;
                                    the API refuses it for hourly employees as well. */}
                                {!auth.isHourly && (
                                    <Button color="success" size="sm" onClick={() => navigate("/attendance/coa/new")}>
                                        <i className="ri-add-line me-1" />New COA Request
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Tabs */}
                        <ResponsiveTabs
                            tabs={TABS.filter(t => t.show(auth)).map(({ key, label }) => ({
                                key,
                                // Each label is a function of the tabCounts map; we
                                // preserve that signature exactly so existing TAB
                                // definitions don't have to change.
                                label: label(tabCounts),
                            }))}
                            activeKey={activeTab}
                            onChange={(key) => { setSearch(""); setActiveTab(key); }}
                            variant="underline"
                            color="primary"
                        />

                        {/* Search */}
                        <InputGroup className="mb-3" style={{ maxWidth: 460 }}>
                            <span className="input-group-text bg-transparent border-end-0">
                                <i className="ri-search-line text-muted" />
                            </span>
                            <Input
                                placeholder="Search by ID, date, type, comment..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="border-start-0"
                                style={{ fontSize: 13 }}
                            />
                        </InputGroup>

                        {/* Table */}
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={6} columns={11} /></tbody>
                                </table>
                            </div>
                        ) : paginated.length === 0 ? (
                            <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>
                                No records found.
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ fontSize: 12 }}>#</th>
                                                {!isCOATab && <th style={{ fontSize: 12 }}>Type</th>}
                                                <th style={{ fontSize: 12 }}>Start date</th>
                                                <th style={{ fontSize: 12 }}>End date</th>
                                                {!isCOATab && <th style={{ fontSize: 12 }}>Days</th>}
                                                {isCOATab && <th style={{ fontSize: 12 }}>COA time</th>}
                                                <th style={{ fontSize: 12 }}>Supervisor</th>
                                                <th style={{ fontSize: 12 }}>HR</th>
                                                <th style={{ fontSize: 12 }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.map(r => {
                                                const canEdit = r.supervisorApproval == null && r.hrApproval == null;
                                                // Owner rule (2026-10-01): the employee deletes only while nobody reviewed
                                                // the request; afterwards HR or IT Admin remove it.
                                                const del = deleteRuleFor(r, auth);
                                                // ✅ isCoa is now always correct because every COA record
                                                //    has typeOfLeave: "COA" set at fetch time (all tabs).
                                                const isCoa = r.typeOfLeave === "COA";
                                                return (
                                                    <tr key={`${isCoa ? "coa" : "fto"}-${r.id}`}>
                                                        <td>
                                                            <span
                                                                className="fw-medium text-primary"
                                                                style={{ fontSize: 13, cursor: "pointer" }}
                                                            >
                                                                {r.id}
                                                            </span>
                                                        </td>
                                                        {!isCOATab && (
                                                            <td>
                                                                <TypeBadge type={r.typeOfLeave} otherReason={r.otherReason} />
                                                            </td>
                                                        )}
                                                        <td style={{ fontSize: 13 }}>{fmtDate(r.startDate)}</td>
                                                        <td style={{ fontSize: 13 }}>{fmtDate(r.endDate)}</td>
                                                        {!isCOATab && (
                                                            <td style={{ fontSize: 13 }}>
                                                                {typeof r.ftoCount === "number" ? r.ftoCount : "—"}
                                                            </td>
                                                        )}
                                                        {isCOATab && (
                                                            <td style={{ fontSize: 13 }}>
                                                                {r.modifiedStartTime ?? "—"} – {r.modifiedEndTime ?? "—"}
                                                            </td>
                                                        )}
                                                        <td><StatusBadge val={r.supervisorApproval} /></td>
                                                        <td><StatusBadge val={r.hrApproval} /></td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                {isApprovalQueue && (
                                                                    <>
                                                                        <IconBtn
                                                                            id={`appr-${isCoa ? "c" : "f"}-${r.id}`}
                                                                            title="Approve" color="#0ab39c"
                                                                            onClick={() => handleQuickDecision(r.id, true)}
                                                                        >
                                                                            <i className="ri-check-line" style={{ fontSize: 13 }} />
                                                                        </IconBtn>
                                                                        <IconBtn
                                                                            id={`rej-${isCoa ? "c" : "f"}-${r.id}`}
                                                                            title="Reject" color="#f06548"
                                                                            onClick={() => handleQuickDecision(r.id, false)}
                                                                        >
                                                                            <i className="ri-close-line" style={{ fontSize: 13 }} />
                                                                        </IconBtn>
                                                                    </>
                                                                )}
                                                                <IconBtn
                                                                    id={`view-${isCoa ? "c" : "f"}-${r.id}`}
                                                                    title="View" color="#405189"
                                                                    onClick={() => navigate(
                                                                        isCoa
                                                                            ? `/attendance/coa/${r.id}`
                                                                            : `/attendance/fto/${r.id}`
                                                                    )}
                                                                >
                                                                    <i className="ri-eye-line" style={{ fontSize: 13 }} />
                                                                </IconBtn>
                                                                <IconBtn
                                                                    id={`edit-${isCoa ? "c" : "f"}-${r.id}`}
                                                                    title={canEdit ? "Edit" : "Read only"}
                                                                    color="#405189"
                                                                    disabled={!canEdit}
                                                                    onClick={() => navigate(
                                                                        isCoa
                                                                            ? `/attendance/coa/${r.id}`
                                                                            : `/attendance/fto/${r.id}`
                                                                    )}
                                                                >
                                                                    <i className="ri-pencil-line" style={{ fontSize: 13 }} />
                                                                </IconBtn>
                                                                <IconBtn
                                                                    id={`del-${isCoa ? "c" : "f"}-${r.id}`}
                                                                    title={del.why}
                                                                    color="#842029"
                                                                    disabled={!del.allowed}
                                                                    onClick={() => confirmDelete(r.id, isCoa ? "coa" : "fto", del.mode)}
                                                                >
                                                                    <i className="ri-delete-bin-line" style={{ fontSize: 13 }} />
                                                                </IconBtn>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                                    <span style={{ fontSize: 13, color: "var(--ssw-fg-2, #878a99)" }}>
                                        Showing {Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length} entries
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" color="light" disabled={currentPage === 1} onClick={() => goToPage(1)}>«</Button>
                                        <Button size="sm" color="light" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>‹</Button>
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                                            const p = start + i;
                                            return (
                                                <Button
                                                    key={p} size="sm"
                                                    color={p === currentPage ? "primary" : "light"}
                                                    onClick={() => goToPage(p)}
                                                >
                                                    {p}
                                                </Button>
                                            );
                                        })}
                                        <Button size="sm" color="light" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>›</Button>
                                        <Button size="sm" color="light" disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>»</Button>
                                    </div>
                                </div>
                            </>
                        )}

                    </CardBody>
                </Card>

                {/* ── Delete confirmation modal ──────────────────────────────── */}
                <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
                    <ModalHeader toggle={() => setDeleteModal(false)}>
                        Confirm Delete
                    </ModalHeader>
                    <ModalBody>
                        <div className="d-flex align-items-center gap-3">
                            <div className="avatar-sm flex-shrink-0">
                                <span className="avatar-title rounded-circle bg-danger-subtle text-danger fs-4">
                                    <i className="ri-delete-bin-line" />
                                </span>
                            </div>
                            <div>
                                <h6 className="mb-1">Delete this request?</h6>
                                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                    This action cannot be undone. The request will be permanently removed.
                                    {deleteMode === "hr" && " Removed by HR / IT Admin: any FTO balance deduction is reversed."}
                                </p>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setDeleteModal(false)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button color="danger" onClick={handleDelete} disabled={deleting}>
                            {deleting && <Spinner size="sm" className="me-1" />}
                            Yes, Delete
                        </Button>
                    </ModalFooter>
                </Modal>

            </Container>
        </div>
    );
};

export default AttendanceIndex;
